/**
 * Allowed-origin grammar (ADR-5.5): `https://host[:port]` or `https://*.domain[:port]`, and
 * `http://localhost:<port>` in development only. Anything else (paths, quotes, commas,
 * semicolons, spaces, userinfo) is rejected, so a stored value is always safe to put in a
 * CSP header or compare with an Origin.
 */
const ORIGIN =
	/^(https?):\/\/(\*\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)*)(?::(\d{1,5}))?$/;

interface OriginParts {
	scheme: string;
	wildcard: boolean;
	host: string;
	/** Empty for the scheme's default port. */
	port: string;
}

function parts(value: string): OriginParts | null {
	const match = ORIGIN.exec(value);
	if (!match?.[1] || !match[3]) {
		return null;
	}
	const [, scheme, star, host, port = ""] = match;
	if (port && (Number(port) < 1 || Number(port) > 65535)) {
		return null;
	}
	const defaultPort = scheme === "https" ? "443" : "80";
	return {
		scheme,
		wildcard: Boolean(star),
		host,
		port: port === defaultPort ? "" : port,
	};
}

/**
 * Normalizes an admin-entered origin (trimmed, lowercased, one trailing slash and the default
 * port dropped). Returns null when it does not fit the grammar. A wildcard needs at least two
 * labels after it (`https://*.com` is refused).
 */
export function parseAllowedOrigin(
	value: string,
	{ allowDev = false }: { allowDev?: boolean } = {},
): string | null {
	const p = parts(value.trim().toLowerCase().replace(/\/$/, ""));
	if (!p) {
		return null;
	}
	if (p.scheme === "http") {
		return allowDev && p.host === "localhost" && p.port && !p.wildcard
			? `http://localhost:${p.port}`
			: null;
	}
	if (p.wildcard && !p.host.includes(".")) {
		return null;
	}
	return `https://${p.wildcard ? "*." : ""}${p.host}${p.port ? `:${p.port}` : ""}`;
}

/**
 * Whether a request or message Origin matches one allowed pattern: exact, or for
 * `https://*.d` a host ending in `"." + d` with the same scheme and port. The apex `d`
 * itself is not matched by the wildcard.
 */
export function originMatches(origin: string, pattern: string): boolean {
	const o = parts(origin);
	const p = parts(pattern);
	if (!o || !p || o.wildcard) {
		return false;
	}
	if (o.scheme !== p.scheme || o.port !== p.port) {
		return false;
	}
	return p.wildcard ? o.host.endsWith(`.${p.host}`) : o.host === p.host;
}
